import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Badge } from '@/components/ui'
import { FileText, ClipboardList, Upload, Plus, Trash2, Camera, X, ChevronRight, ChevronDown, Info, CopyPlus } from 'lucide-react'
import unitIcon from '@/assets/icons/inspection-icons/unit.svg'
import toolIcon from '@/assets/icons/inspection-icons/tool.svg'
import arcticIcon from '@/assets/icons/inspection-icons/arctic.svg'
import greenpinIcon from '@/assets/icons/inspection-icons/greenpin.svg'
import { isSlingCategory, matchesCategoryFamily } from '@/utils/categoryFamily'
import chainslingIcon from '@/assets/icons/inspection-icons/chainsling.svg'
import wireslingIcon from '@/assets/icons/inspection-icons/wiresling.svg'
import docIconCert from '@/assets/icons/document-icons/CERT.svg'
import docIconDoc  from '@/assets/icons/document-icons/DOC.svg'
import docIconCo   from '@/assets/icons/document-icons/CO.svg'
import docIconNdt  from '@/assets/icons/document-icons/NDT.svg'
import docIconSr   from '@/assets/icons/document-icons/SR.svg'
import docIconMan  from '@/assets/icons/document-icons/MAN.svg'
import { format } from 'date-fns'
import SpecificationsPanel from './SpecificationsPanel'
import InspectionModal from './InspectionModal'
import SubEquipmentModal from './SubEquipmentModal'
import { AppBarTitle } from '@/components/layout/pageHeader'
import DocumentScanner from './DocumentScanner'
import { uploadDocument } from '@/services/document-upload.service'
import { updateEquipmentNdtDates } from '@/utils/ndtEquipmentReport'
import { fetchSTMIDBEquipmentById } from '@/hooks/useEquipment'
import { loadExternalSpecs } from '@/hooks/useEquipmentSpecifications'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  getSTMIDBDocumentSignedUrl,
  getSTMIDBUploadCode,
  isSTMIDBCompany,
  mapSTMIDBDocumentTypeToWLL,
  mapSTMIDBToWLLEquipment,
  mapWLLDocumentTypeToSTMIDB,
} from '@/services/api/stmidbEquipmentService'

// Touch-friendly dimensions matching Samleliste
const TOUCH = {
  buttonHeight: 'min-h-[48px]',
  inputHeight: 'min-h-[48px]',
  fontSize: 'text-base',
}

interface Equipment {
  id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
  company_id?: string
}

interface Documentation {
  id: string
  equipment_id: string
  document_type: string
  file_name: string
  storage_path: string
  version: string | null
  is_current: boolean
  created_at: string
  inspection_status: string | null
}

interface Relationship {
  id: string
  main_equipment_id: string
  sub_equipment_id: string
  created_at: string
  sub_equipment?: Equipment & { categories_v2?: { code: string } | null }
  wll?: string | null
  quantity?: number | null
  manufacturer?: string | null
  categoryCode?: string | null
  ndtDate?: string | null
  nextNdtDate?: string | null
  certNumber?: string | null
  certStoragePath?: string | null
}

interface ParentEquipment {
  id: string
  main_equipment_id: string
  sub_equipment_id: string
  created_at: string
  main_equipment?: Equipment
}

interface EquipmentDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  equipment: Equipment
  onDelete?: () => void
  onCopied?: (equipment: Equipment) => void
  fullscreen?: boolean
}

const EquipmentDetailsModal = ({ isOpen, onClose, equipment, onDelete, onCopied, fullscreen = false }: EquipmentDetailsModalProps) => {
  const isSLB = isSTMIDBCompany(equipment.company_id)
  const specSource = isSLB ? 'stmidb' as const : undefined
  const [documentation, setDocumentation] = useState<Documentation[]>([])
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({})
  const [subEquipment, setSubEquipment] = useState<Relationship[]>([])
  const [parentEquipment, setParentEquipment] = useState<ParentEquipment[]>([])
  const [loading, setLoading] = useState(true)
  const [currentEquipment, setCurrentEquipment] = useState<Equipment>(equipment)
  const [currentEquipmentId, setCurrentEquipmentId] = useState<string>(equipment.id)
  const [currentCategoryCode, setCurrentCategoryCode] = useState<string | null>(null)
  const [showInspectionModal, setShowInspectionModal] = useState(false)
  const [showSubEquipmentModal, setShowSubEquipmentModal] = useState(false)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [showDocumentScanner, setShowDocumentScanner] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('certificate')
  const [companyId, setCompanyId] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showCopyDialog, setShowCopyDialog] = useState(false)
  const [copySerial, setCopySerial] = useState('')
  const [copyOwnerId, setCopyOwnerId] = useState('')
  const [copyPartNumber, setCopyPartNumber] = useState('')
  const [copying, setCopying] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'info' | 'dokumenter'>('info')
  const [replacingRelationshipId, setReplacingRelationshipId] = useState<string | null>(null)
  const [infoSectionOpen, setInfoSectionOpen] = useState<string | null>(null)
  const [unitHeaderSpecs, setUnitHeaderSpecs] = useState<{
    tare: string | null
    payload: string | null
    mgw: string | null
    certificate: string | null
    nextNdt: string | null
  }>({ tare: null, payload: null, mgw: null, certificate: null, nextNdt: null })

  useEffect(() => {
    if (isOpen) {
      console.log('🔧 EquipmentDetailsModal - Equipment:', {
        id: equipment.id,
        name: equipment.name,
        category_id: equipment.category_id,
        asset_number: equipment.asset_number
      })
      setCurrentEquipment(equipment)
      setCurrentEquipmentId(equipment.id)
      setCurrentCategoryCode(null)
      setActiveTab('info')
      setInfoSectionOpen(null)
      setShowCopyDialog(false)
      setUnitHeaderSpecs({ tare: null, payload: null, mgw: null, certificate: null, nextNdt: null })
      fetchEquipmentDetails(equipment.id)
      checkUserRole()
    }
  }, [isOpen, equipment.id])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file')
      return
    }

    setUploadingFile(true)
    try {
      if (isSLB) {
        await STMIDBEquipmentService.uploadDocument({
          equipmentId: currentEquipmentId,
          equipmentCode: getSTMIDBUploadCode(currentEquipment),
          documentType: mapWLLDocumentTypeToSTMIDB(selectedDocumentType),
          file: selectedFile,
        })
        alert('Document uploaded successfully!')
        setShowUploadDialog(false)
        setSelectedFile(null)
        setSelectedDocumentType('certificate')
        await fetchEquipmentDetails(currentEquipmentId)
        return
      }

      // Get equipment company for upload path — use currentEquipment so navigated sub-equipment works correctly
      const { data: equipmentData } = await supabase
        .from('equipment_v2')
        .select('company_id, companies!inner(name)')
        .eq('id', currentEquipmentId)
        .single()

      const companyName = (equipmentData?.companies as any)?.name || 'unknown'

      const result = await uploadDocument({
        file: selectedFile,
        customerName: companyName,
        serialNumber: currentEquipment.serial_number || currentEquipment.asset_number || 'unknown',
        equipmentId: currentEquipmentId,
        documentType: selectedDocumentType,
      })

      if (result.success) {
        alert('Document uploaded successfully!')
        setShowUploadDialog(false)
        setSelectedFile(null)
        setSelectedDocumentType('certificate')
        // Refresh documents
        await fetchEquipmentDetails(currentEquipmentId)
      } else {
        alert(`Upload failed: ${result.error}`)
      }
    } catch (error) {
      console.error('Upload error:', error)
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploadingFile(false)
    }
  }

  const addYearsToIsoDate = (isoDate: string, years: number) => {
    const [year, month, day] = isoDate.split('-').map(Number)
    if (!year || !month || !day) return null
    return `${year + years}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  const loadUnitHeaderSpecs = async (equipmentId: string) => {
    const formatHeaderSpec = (spec: any) => {
      if (!spec) return null
      const raw = spec.value_numeric ?? spec.value_integer ?? spec.value_text ?? spec.value_date
      if (raw === null || raw === undefined || raw === '') return null

      const asNumber = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'))
      const display = Number.isFinite(asNumber) && spec.value_date == null
        ? String(asNumber)
        : String(raw).trim()
      if (!display) return null

      const unitRel = spec.unit
      const unit = Array.isArray(unitRel) ? unitRel[0]?.symbol : unitRel?.symbol
      return unit ? `${display} ${unit}`.trim() : display
    }

    const applyRows = (rows: any[] | null, nextNdt: string | null) => {
      const byKey = (...keys: string[]) =>
        rows?.find((s: any) => keys.includes(String(s.spec_key || '').toLowerCase()))
      setUnitHeaderSpecs({
        tare: formatHeaderSpec(byKey('tare', 'tare_weight')),
        payload: formatHeaderSpec(byKey('payload')),
        mgw: formatHeaderSpec(byKey('mgw')),
        certificate: formatHeaderSpec(byKey('certificate', 'cert_no', 'certificate_no')),
        nextNdt,
      })
    }

    if (isSLB) {
      const [eqRow, rows] = await Promise.all([
        STMIDBEquipmentService.getEquipmentById(equipmentId),
        loadExternalSpecs(equipmentId),
      ])
      const lastNdtSpec = rows.find(s => ['ndt_date', 'last_ndt_date'].includes(s.spec_key.toLowerCase()))
      const lastNdt = lastNdtSpec?.value_date || lastNdtSpec?.value_text || eqRow?.last_ndt_date || null
      const nextNdt = eqRow?.next_ndt_date || (lastNdt ? addYearsToIsoDate(String(lastNdt).slice(0, 10), 4) : null)
      applyRows(rows, nextNdt)
      if (eqRow) {
        setCurrentEquipment((prev) =>
          prev.id === equipmentId
            ? { ...prev, last_ndt_date: eqRow.last_ndt_date, next_ndt_date: eqRow.next_ndt_date }
            : prev,
        )
      }
      return
    }

    const specSelect = `
      spec_key,
      value_numeric,
      value_integer,
      value_text,
      value_date,
      unit:units_v2!equipment_specifications_v2_unit_id_fkey(symbol)
    `

    const [{ data: eqRow }, withUnit] = await Promise.all([
      supabase
        .from('equipment_v2')
        .select('last_ndt_date, next_ndt_date')
        .eq('id', equipmentId)
        .single(),
      supabase
        .from('equipment_specifications_v2')
        .select(specSelect)
        .eq('equipment_id', equipmentId),
    ])

    let rows: Array<Record<string, any>> | null = (withUnit.data as Array<Record<string, any>> | null)
    if (withUnit.error) {
      console.warn('Unit header specs with unit join failed, retrying:', withUnit.error)
      const fallback = await supabase
        .from('equipment_specifications_v2')
        .select('spec_key, value_numeric, value_integer, value_text, value_date')
        .eq('equipment_id', equipmentId)

      if (fallback.error) {
        console.error('Failed to load unit header specs:', fallback.error)
        return
      }
      rows = fallback.data as Array<Record<string, any>> | null
    }

    const lastNdtSpec = rows?.find((s: any) =>
      ['ndt_date', 'last_ndt_date'].includes(String(s.spec_key || '').toLowerCase()),
    )
    const lastNdt = lastNdtSpec?.value_date || lastNdtSpec?.value_text || eqRow?.last_ndt_date || null
    const nextNdt = eqRow?.next_ndt_date || (lastNdt ? addYearsToIsoDate(String(lastNdt).slice(0, 10), 4) : null)

    applyRows(rows, nextNdt)

    if (eqRow) {
      setCurrentEquipment((prev) =>
        prev.id === equipmentId
          ? { ...prev, last_ndt_date: eqRow.last_ndt_date, next_ndt_date: eqRow.next_ndt_date }
          : prev,
      )
    }
  }

  const handleSpecsUpdated = async (
    change?: { specKey: string; value: any },
    targetEquipmentId: string = currentEquipmentId,
  ) => {
    const key = String(change?.specKey || '').toLowerCase()
    const isNdt = key === 'ndt_date' || key === 'last_ndt_date'
    const dateValue = change?.value == null || change.value === ''
      ? null
      : String(change.value).slice(0, 10)

    // SLB: the external_equipment_specifications trigger already syncs
    // STMIDB's last_ndt_date / next_ndt_date from the NDT Dato spec.
    if (!isSLB && isNdt && dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      try {
        await updateEquipmentNdtDates(targetEquipmentId, dateValue)
      } catch (error) {
        console.error('Failed to sync NDT dates after spec save:', error)
      }
    }

    if (targetEquipmentId === currentEquipmentId) {
      await loadUnitHeaderSpecs(currentEquipmentId)
      return
    }

    await fetchEquipmentDetails(currentEquipmentId, { silent: true })
  }

  const fetchSTMIDBEquipmentDetails = async (equipmentId: string) => {
    const equipmentData = await fetchSTMIDBEquipmentById(equipmentId)
    if (!equipmentData) {
      console.error('STMIDB equipment not found:', equipmentId)
      return
    }

    setCurrentEquipment(equipmentData)
    setCurrentCategoryCode(equipmentData.category_code)
    setCompanyId(STMIDB_WLL_COMPANY_ID)

    await loadUnitHeaderSpecs(equipmentId)

    const [stmidbDocs, children, parentHits] = await Promise.all([
      STMIDBEquipmentService.getDocuments(equipmentId),
      STMIDBEquipmentService.getSubEquipment(equipmentId),
      equipmentData.serial_number || equipmentData.asset_number
        ? STMIDBEquipmentService.searchReportEquipment(
            (equipmentData.serial_number || equipmentData.asset_number) as string,
            20,
          ).catch(() => [])
        : Promise.resolve([]),
    ])

    const docs: Documentation[] = stmidbDocs.map(doc => ({
      id: doc.id,
      equipment_id: doc.equipment_id,
      document_type: mapSTMIDBDocumentTypeToWLL(doc.document_type),
      file_name: doc.file_name || '',
      storage_path: doc.file_path || '',
      version: null,
      is_current: true,
      created_at: doc.created_at,
      inspection_status: null,
    }))

    // STMIDB has no join table: the child id doubles as the relationship id.
    const relationships: Relationship[] = await Promise.all(
      children.map(async (child) => {
        const mapped = mapSTMIDBToWLLEquipment(child)
        const specs = await loadExternalSpecs(child.id)
        const byKey = (...keys: string[]) => specs.find(s => keys.includes(s.spec_key))
        const wllSpec = byKey('wll', 'swl')
        const wllValue = wllSpec?.value_numeric ?? wllSpec?.value_integer ?? wllSpec?.value_text
        const quantitySpec = byKey('quantity', 'qty')
        const manufacturerSpec = byKey('manufacturer')
        let manufacturer = manufacturerSpec?.value_text || null
        if (manufacturerSpec?.value_reference_id) {
          const { data: mfr } = await supabase
            .from('manufacturers_v2')
            .select('name')
            .eq('id', manufacturerSpec.value_reference_id)
            .maybeSingle()
          manufacturer = mfr?.name || manufacturer
        }
        const ndtSpec = byKey('last_ndt_date', 'ndt_date')
        const certSpec = byKey('certificate')
        return {
          id: child.id,
          main_equipment_id: equipmentId,
          sub_equipment_id: child.id,
          created_at: child.created_at,
          sub_equipment: { ...mapped, categories_v2: mapped.category_code ? { code: mapped.category_code } : null },
          wll: wllValue != null && wllValue !== '' ? `${wllValue} ${wllSpec?.unit?.symbol || ''}`.trim() : null,
          quantity: quantitySpec?.value_integer ?? quantitySpec?.value_numeric ?? null,
          manufacturer,
          categoryCode: mapped.category_code,
          ndtDate: ndtSpec?.value_date || ndtSpec?.value_text || child.last_ndt_date,
          nextNdtDate: child.next_ndt_date,
          certNumber: certSpec?.value_text || certSpec?.value_numeric?.toString() || null,
          certStoragePath: null,
        }
      }),
    )

    const parentId = parentHits.find(hit => hit.id === equipmentId)?.parent_equipment_id
    const parent = parentId ? await fetchSTMIDBEquipmentById(parentId) : null
    const parents: ParentEquipment[] = parent
      ? [{
          id: parent.id,
          main_equipment_id: parent.id,
          sub_equipment_id: equipmentId,
          created_at: parent.created_at,
          main_equipment: { ...parent, categories_v2: { code: parent.category_code, title_en: parent.category_title } } as Equipment,
        }]
      : []

    setDocumentation(docs)
    setSubEquipment(relationships)
    setParentEquipment(parents)

    const urls: Record<string, string> = {}
    await Promise.all(docs.map(async (doc) => {
      const url = await getSTMIDBDocumentSignedUrl(doc.storage_path)
      if (url) urls[doc.id] = url
    }))
    setDocumentUrls(urls)
  }

  const fetchEquipmentDetails = async (equipmentId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true)
    try {
      if (isSLB) {
        await fetchSTMIDBEquipmentDetails(equipmentId)
        return
      }

      // Fetch full equipment details
      const { data: equipmentData, error: equipmentError } = await supabase
        .from('equipment_v2')
        .select(`
          *,
          categories_v2 (id, code, title_en, title_no)
        `)
        .eq('id', equipmentId)
        .single()

      if (equipmentError || !equipmentData) {
        console.error('Error fetching equipment:', equipmentError)
        setLoading(false)
        return
      }

      // Update current equipment with full details
      setCurrentEquipment(equipmentData as Equipment)
      setCurrentCategoryCode((equipmentData as any).categories_v2?.code ?? null)
      setCompanyId(equipmentData.company_id)

      await loadUnitHeaderSpecs(equipmentId)

      // Note: Specifications are now handled by SpecificationsPanel component
      // which uses the useEquipmentSpecifications hook for efficient loading/updating

      // Fetch documentation
      const { data: docs } = await supabase
        .from('equipment_documents')
        .select('*')
        .eq('equipment_id', equipmentId)
        .eq('is_current', true)
        .order('created_at', { ascending: false })

      // Fetch sub-equipment relationships
      const { data: relationships } = await supabase
        .from('equipment_relationship_v2')
        .select(`
          *,
          sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
            id,
            name,
            asset_number,
            serial_number,
            status,
            category_id,
            next_ndt_date,
            categories_v2(code)
          )
        `)
        .eq('main_equipment_id', equipmentId)

      // Fetch additional details for sub-equipment (WLL, quantity, manufacturer)
      const relationshipsWithDetails = await Promise.all(
        (relationships || []).map(async (rel) => {
          const subEquipmentId = rel.sub_equipment_id
          const { data: specs } = await supabase
            .from('equipment_specifications_v2')
            .select(`
              spec_key, value_numeric, value_integer, value_text, value_date, value_reference_id,
              unit:units_v2!equipment_specifications_v2_unit_id_fkey(symbol)
            `)
            .eq('equipment_id', subEquipmentId)
            .in('spec_key', ['wll', 'swl', 'quantity', 'qty', 'manufacturer', 'last_ndt_date', 'ndt_date', 'certificate'])

          const wllSpec = specs?.find(s => ['wll', 'swl'].includes(s.spec_key))
          const quantitySpec = specs?.find(s => ['quantity', 'qty'].includes(s.spec_key))
          const manufacturerSpec = specs?.find(s => s.spec_key === 'manufacturer')
          
          let wllDisplay = null
          if (wllSpec) {
            const value = wllSpec.value_numeric || wllSpec.value_integer || wllSpec.value_text
            const unit = (wllSpec.unit as any)?.symbol || ''
            wllDisplay = value ? `${value} ${unit}`.trim() : null
          }
          const quantity = quantitySpec?.value_integer || quantitySpec?.value_numeric || null
          
          let manufacturerName = null
          if (manufacturerSpec?.value_reference_id) {
            const { data: mfr } = await supabase
              .from('manufacturers_v2')
              .select('name')
              .eq('id', manufacturerSpec.value_reference_id)
              .single()
            manufacturerName = mfr?.name || null
          } else if (manufacturerSpec?.value_text) {
            // Try fetching by name or ID stored in value_text
            const textValue = manufacturerSpec.value_text
            // Check if it's a UUID (legacy data)
            if (textValue.length === 36 && textValue.includes('-')) {
              const { data: mfr } = await supabase
                .from('manufacturers_v2')
                .select('name')
                .eq('id', textValue)
                .single()
              manufacturerName = mfr?.name || null
            } else {
              // It's already a manufacturer name
              manufacturerName = textValue
            }
          }

          const ndtSpec = specs?.find(s => ['last_ndt_date', 'ndt_date'].includes(s.spec_key))
          const ndtDate = (ndtSpec as any)?.value_date || ndtSpec?.value_text || null
          const certSpec = specs?.find(s => s.spec_key === 'certificate')
          const certNumber = certSpec?.value_text || certSpec?.value_numeric?.toString() || null

          const { data: certDoc } = await supabase
            .from('equipment_documents')
            .select('storage_path')
            .eq('equipment_id', subEquipmentId)
            .eq('document_type', 'certificate')
            .eq('is_current', true)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          return {
            ...rel,
            wll: wllDisplay,
            quantity: quantity,
            manufacturer: manufacturerName,
            categoryCode: (rel.sub_equipment as any)?.categories_v2?.code || null,
            ndtDate,
            nextNdtDate: (rel.sub_equipment as any)?.next_ndt_date || null,
            certNumber,
            certStoragePath: certDoc?.storage_path || null,
          }
        })
      )

      // Fetch parent equipment (hlavutstyr) - where this equipment is the sub
      const { data: parentRelationships } = await supabase
        .from('equipment_relationship_v2')
        .select(`
          *,
          main_equipment:equipment_v2!equipment_relationship_v2_main_equipment_id_fkey(
            id,
            name,
            asset_number,
            serial_number,
            status,
            categories_v2 (code, title_en)
          )
        `)
        .eq('sub_equipment_id', equipmentId)

      setDocumentation(docs || [])
      setSubEquipment(relationshipsWithDetails || [])
      setParentEquipment(parentRelationships || [])

      // Generate signed URLs for documents
      if (docs && docs.length > 0) {
        const urls: Record<string, string> = {}
        for (const doc of docs) {
          const { data: signedUrl } = await supabase.storage
            .from('documentation')
            .createSignedUrl(doc.storage_path, 3600) // 1 hour expiry
          
          if (signedUrl?.signedUrl) {
            urls[doc.id] = signedUrl.signedUrl
          }
        }
        setDocumentUrls(urls)
      }
    } catch (error) {
      console.error('Failed to fetch equipment details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleNavigateToSubEquipment = async (subEquipmentId: string) => {
    setCurrentEquipmentId(subEquipmentId)
    setInfoSectionOpen(null)
    setActiveTab('info')
    await fetchEquipmentDetails(subEquipmentId)
  }

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single()
        
        if (profile?.role === 'admin') {
          setIsAdmin(true)
        }
      }
    } catch (error) {
      console.error('Error checking user role:', error)
    }
  }

  const handleDelete = async () => {
    const confirmMessage = `Er du sikker på at du vil slette dette utstyret?\n\nSerienummer: ${currentEquipment.serial_number || currentEquipment.asset_number}\nNavn: ${currentEquipment.name}\n\nDette kan ikke angres.`
    
    if (!confirm(confirmMessage)) {
      return
    }

    setDeleting(true)
    try {
      if (isSLB) {
        // The delete-equipment edge function cascades documents, inspections
        // and control_info on the STMIDB side.
        await STMIDBEquipmentService.deleteEquipment(currentEquipmentId)
        alert('Utstyr slettet')
        onClose()
        onDelete?.()
        return
      }

      // Delete equipment relationships first (both as main and as sub)
      await supabase
        .from('equipment_relationship_v2')
        .delete()
        .or(`main_equipment_id.eq.${currentEquipmentId},sub_equipment_id.eq.${currentEquipmentId}`)

      // Delete equipment specifications
      await supabase
        .from('equipment_specifications_v2')
        .delete()
        .eq('equipment_id', currentEquipmentId)

      // Delete the equipment itself
      const { error } = await supabase
        .from('equipment_v2')
        .delete()
        .eq('id', currentEquipmentId)

      if (error) throw error

      alert('Utstyr slettet')
      
      // Close modal and call onDelete callback to refresh the list
      onClose()
      if (onDelete) {
        onDelete()
      }
    } catch (error) {
      console.error('Error deleting equipment:', error)
      alert('Kunne ikke slette utstyr: ' + (error instanceof Error ? error.message : 'Ukjent feil'))
    } finally {
      setDeleting(false)
    }
  }

  const openCopyDialog = () => {
    setCopySerial('')
    setCopyOwnerId(currentEquipment.owner_id || '')
    setCopyPartNumber(currentEquipment.part_number || '')
    setCopyError(null)
    setShowCopyDialog(true)
  }

  const handleCopy = async () => {
    const serial = copySerial.trim()
    if (!serial) {
      setCopyError('Serienummer er påkrevd')
      return
    }

    setCopying(true)
    setCopyError(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Bruker ikke autentisert')

      const { data: source, error: sourceError } = await supabase
        .from('equipment_v2')
        .select('name, part_number, company_id, category_id, manufacturer_id, status, location, owner_id, description, model, manufacture_year')
        .eq('id', currentEquipmentId)
        .single()
      if (sourceError || !source) throw new Error('Kunne ikke hente utstyr å kopiere')

      const { data: duplicate } = await supabase
        .from('equipment_v2')
        .select('id')
        .eq('company_id', source.company_id)
        .eq('serial_number', serial)
        .maybeSingle()
      if (duplicate) throw new Error('Serienummer finnes allerede for dette selskapet')

      const { data: assetNumber, error: assetError } = await supabase.rpc(
        'generate_company_asset_number',
        { company_id_param: source.company_id },
      )
      if (assetError) throw new Error('Kunne ikke generere ID-nummer')

      const { data: created, error: createError } = await supabase
        .from('equipment_v2')
        .insert({
          asset_number: assetNumber,
          serial_number: serial,
          name: source.name,
          part_number: copyPartNumber.trim() || null,
          company_id: source.company_id,
          category_id: source.category_id,
          manufacturer_id: source.manufacturer_id,
          status: source.status || 'Aktiv',
          location: source.location || null,
          owner_id: copyOwnerId.trim() || null,
          description: source.description || null,
          model: source.model || null,
          manufacture_year: source.manufacture_year || null,
          created_by: user.id,
        })
        .select('id, name, serial_number, part_number, asset_number, company_id, owner_id, category_id, status, last_ndt_date, next_ndt_date, last_inspection_date, next_inspection_date, description, model, manufacture_year')
        .single()
      if (createError || !created) throw new Error('Kunne ikke opprette kopi')

      const { data: specs } = await supabase
        .from('equipment_specifications_v2')
        .select('template_id, spec_key, unit_id, value_text, value_numeric, value_integer, value_boolean, value_date, value_reference_id')
        .eq('equipment_id', currentEquipmentId)

      if (specs && specs.length > 0) {
        const { error: specsError } = await supabase
          .from('equipment_specifications_v2')
          .insert(specs.map(spec => ({
            ...spec,
            equipment_id: created.id,
            created_by: user.id,
            updated_by: user.id,
            validation_status: 'pending',
          })))
        if (specsError) console.error('Specs copy error:', specsError)
      }

      const copied: Equipment = {
        id: created.id,
        name: created.name,
        part_number: created.part_number,
        asset_number: created.asset_number,
        serial_number: created.serial_number,
        owner_id: created.owner_id,
        last_inspection_date: created.last_inspection_date ?? null,
        next_inspection_date: created.next_inspection_date ?? null,
        last_ndt_date: created.last_ndt_date ?? null,
        next_ndt_date: created.next_ndt_date ?? null,
        status: created.status || 'Aktiv',
        inspection_status: null,
        description: created.description ?? null,
        model: created.model ?? null,
        manufacture_year: created.manufacture_year ?? null,
        category_id: created.category_id ?? null,
        company_id: created.company_id,
      }

      setShowCopyDialog(false)
      setCopySerial('')
      onCopied?.(copied)
      setCurrentEquipment(copied)
      setCurrentEquipmentId(copied.id)
      setCurrentCategoryCode(null)
      setActiveTab('info')
      setInfoSectionOpen(null)
      await fetchEquipmentDetails(copied.id)
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : 'Kunne ikke kopiere utstyr')
    } finally {
      setCopying(false)
    }
  }

  const handleRemoveRelationship = async (relationshipId: string, label: string) => {
    if (!confirm(`Koble fra "${label}"?\n\nDette fjerner bare koblingen, ikke utstyret selv.`)) return
    if (isSLB) {
      try {
        await STMIDBEquipmentService.setParentEquipment(relationshipId, null)
        fetchEquipmentDetails(currentEquipmentId)
      } catch (err) {
        alert('Kunne ikke koble fra: ' + (err instanceof Error ? err.message : 'Ukjent feil'))
      }
      return
    }
    const { error } = await supabase
      .from('equipment_relationship_v2')
      .delete()
      .eq('id', relationshipId)
    if (!error) fetchEquipmentDetails(currentEquipmentId)
  }

  /** Offshore shackle only (R4-2) — not other R4 subtypes like eye bolt, clamp, etc. */
  const isOffshoreShackle = (categoryCode: string | null | undefined) => {
    const code = (categoryCode || '').toUpperCase()
    return code === 'R4-2' || code.startsWith('R4-2-')
  }

  const getRelationIcon = (categoryCode: string | null | undefined, manufacturer?: string | null) => {
    const code = (categoryCode || '').toUpperCase()
    if (matchesCategoryFamily(code, 'R1')) return { src: chainslingIcon, w: 24, h: 24, isSling: true, kind: 'kjetting' as const }
    if (matchesCategoryFamily(code, 'R3')) return { src: wireslingIcon,  w: 12, h: 24, isSling: true, kind: 'wiresling' as const }
    // Offshore shackle (R4-2) only — pick icon by manufacturer
    if (isOffshoreShackle(code)) {
      const mfr = (manufacturer || '').toLowerCase()
      const shackleIcon = mfr.includes('van beest') ? greenpinIcon : arcticIcon
      return { src: shackleIcon, w: 16, h: 20, isSling: false, kind: 'sjakkel' as const }
    }
    return { src: toolIcon, w: 24, h: 24, isSling: false, kind: 'tool' as const }
  }

  const getRelationLabel = (categoryCode: string | null | undefined) => {
    const code = (categoryCode || '').toUpperCase()
    if (matchesCategoryFamily(code, 'R1')) return 'Kjetting'
    if (matchesCategoryFamily(code, 'R3')) return 'Wiresling'
    if (isOffshoreShackle(code)) return 'Sjakler'
    return 'Utstyr'
  }

  const getEquipmentKind = (categoryCode: string | null | undefined): 'unit' | 'tool' | 'sling' | 'sjakler' => {
    const code = (categoryCode || '').toUpperCase()
    if (isSlingCategory(code)) return 'sling'
    if (isOffshoreShackle(code)) return 'sjakler'
    // F-category units (offshore lastebærere), but not FAL*
    if (code.startsWith('F') && !code.startsWith('FAL')) return 'unit'
    return 'tool'
  }

  const getKindIcon = (kind: 'unit' | 'tool' | 'sling' | 'sjakler', categoryCode?: string | null) => {
    if (kind === 'unit') return { src: unitIcon, h: 40, label: 'Hovedutstyr' }
    if (kind === 'tool') return { src: toolIcon, h: 32, label: 'Utstyr' }
    if (kind === 'sling') return { src: getRelationIcon(categoryCode).src, h: 36, label: 'Sling' }
    return { src: arcticIcon, h: 30, label: 'Sjakler' }
  }

  if (!isOpen) return null

  const TABS = [
    { id: 'info'       as const, label: 'Info',       icon: Info      },
    { id: 'dokumenter' as const, label: 'Dokumenter', icon: FileText, count: documentation.length },
  ]

  return (
    <div className={fullscreen
      ? 'fixed right-0 bottom-0 z-40 flex flex-col bg-white'
      : 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60'
    } style={fullscreen ? { top: 'var(--topnav-height)', left: 'var(--rail-width)' } : undefined}>
      {fullscreen && (
        <AppBarTitle
          title="Utstyrsdetaljer"
          subtitle={equipment.name}
          onBack={onClose}
        />
      )}
      <div className={fullscreen
        ? 'bg-white w-full h-full overflow-hidden flex flex-col'
        : 'bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl'
      }>

        {/* ── Header ───────────────────────────────────────────── */}
        {!fullscreen && (
          <div className="flex items-center justify-between px-4 py-2 bg-[#1E3A5F] shrink-0">
            <p className="text-sm font-semibold text-white leading-tight">Utstyrsdetaljer</p>
            <button
              onClick={onClose}
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* ── Tab bar ──────────────────────────────────────────── */}
        <div className={`flex border-b border-slate-200 bg-white shrink-0 px-2 ${fullscreen ? 'lg:hidden' : ''}`}>
          {TABS.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={[
                'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-b-2 transition-colors',
                activeTab === id
                  ? 'border-[#1E3A5F] text-[#1E3A5F]'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              ].join(' ')}
              style={{ minHeight: 52 }}
            >
              <Icon size={17} />
              <span>{label}</span>
              {count != null && count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === id ? 'bg-[#1E3A5F]/10 text-[#1E3A5F]' : 'bg-slate-100 text-slate-500'
                }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ──────────────────────────────────────── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">Laster detaljer...</span>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex">
          <div className={[
            'flex-1 min-w-0 overflow-y-auto px-6 py-5',
            activeTab === 'info' ? '' : fullscreen ? 'hidden lg:block' : 'hidden',
          ].join(' ')}>

            {/* INFO — retractable sections for current equipment + existing relations */}
            {(() => {
              const kind = getEquipmentKind(currentCategoryCode)
              const isMainEquipment = kind === 'unit' || kind === 'tool'
              const slings = subEquipment.filter(r => isSlingCategory(r.categoryCode))
              const shackles = subEquipment.filter(r => isOffshoreShackle(r.categoryCode))
              const otherTools = subEquipment.filter(
                r => !isSlingCategory(r.categoryCode) && !isOffshoreShackle(r.categoryCode)
              )
              const toggle = (section: string) =>
                setInfoSectionOpen(open => (open === section ? null : section))

              const primaryIcon = getKindIcon(kind, currentCategoryCode)

              const RelatedEquipmentRow = ({
                rel,
                typeLabel,
              }: {
                rel: typeof subEquipment[0]
                typeLabel: string
              }) => {
                const icon = getRelationIcon(rel.categoryCode, rel.manufacturer)
                const sub = rel.sub_equipment
                if (!sub) return null
                const label = sub.serial_number || 'Ukjent SN'
                const isShackle = isOffshoreShackle(rel.categoryCode)
                const isSling = isSlingCategory(rel.categoryCode)
                const antall = rel.quantity != null && Number.isFinite(Number(rel.quantity))
                  ? Number(rel.quantity)
                  : 1
                const subSpecItem = (key: string, value: React.ReactNode, colorCls?: string) => (
                  <span key={key}>
                    <span className="font-semibold text-slate-500">{key}:</span>{' '}
                    <span className={colorCls}>{value}</span>
                  </span>
                )

                let ndtSpecItem: React.ReactNode = null
                if (isSling && rel.nextNdtDate) {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  const ndtDate = new Date(rel.nextNdtDate)
                  const in12Months = new Date(today)
                  in12Months.setFullYear(in12Months.getFullYear() + 1)
                  const isOverdue = ndtDate < today
                  const isSoon = !isOverdue && ndtDate <= in12Months
                  const cls = isOverdue
                    ? 'text-red-500 font-semibold'
                    : isSoon
                      ? 'text-amber-400 font-semibold'
                      : ''
                  ndtSpecItem = subSpecItem('Neste NDT', format(ndtDate, 'dd.MM.yyyy'), cls)
                }

                const subSpecItems: React.ReactNode[] = [
                  ...(rel.manufacturer ? [subSpecItem('Produsent', rel.manufacturer)] : []),
                  ...((isShackle || isSling) && rel.wll ? [subSpecItem('WLL', rel.wll)] : []),
                  ...(isShackle ? [subSpecItem('Ant', String(antall))] : []),
                  ...(ndtSpecItem ? [ndtSpecItem] : []),
                  ...(rel.certNumber ? [subSpecItem('Sertifikat', rel.certNumber)] : []),
                ]
                const sectionId = `sub-${rel.id}`
                const open = infoSectionOpen === sectionId
                return (
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="flex items-stretch">
                      <button
                        onClick={() => toggle(sectionId)}
                        className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                        style={{ minHeight: 48 }}
                      >
                        <img
                          src={icon.src}
                          alt=""
                          className="object-contain shrink-0"
                          style={{ height: icon.isSling ? 32 : 26, width: 'auto' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 leading-none mb-1 truncate">
                            {typeLabel}{sub.name ? ` · ${sub.name}` : ''}
                          </p>
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {label}
                          </p>
                          {subSpecItems.length > 0 && (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-0.5 text-xs text-slate-400">
                              {subSpecItems}
                            </div>
                          )}
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-slate-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <div className="shrink-0 self-center flex items-center gap-2 mr-3">
                        <button
                          type="button"
                          onClick={() => {
                            setReplacingRelationshipId(rel.id)
                            setShowSubEquipmentModal(true)
                          }}
                          className="px-4 min-h-[44px] text-sm font-semibold text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 active:bg-slate-100 transition-colors"
                        >
                          Bytt
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveRelationship(rel.id, label)}
                          className="px-4 min-h-[44px] text-sm font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors"
                        >
                          Fjern
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div className="border-t border-slate-100 px-3 py-3">
                        <SpecificationsPanel
                          equipmentId={sub.id}
                          categoryId={sub.category_id}
                          source={specSource}
                          onSpecsUpdated={(change) => void handleSpecsUpdated(change, sub.id)}
                        />
                      </div>
                    )}
                  </div>
                )
              }

              const Section = ({
                id,
                icon,
                iconH,
                label,
                serial,
                name,
                subtitle,
                count,
                children,
              }: {
                id: string
                icon: string
                iconH: number
                label: string
                serial?: string | null
                name?: string | null
                subtitle?: React.ReactNode
                count?: number
                children: React.ReactNode
              }) => {
                const open = infoSectionOpen === id
                const title = serial
                  ? (name ? `${serial} - ${name}` : serial)
                  : '—'
                return (
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggle(id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                      style={{ minHeight: 48 }}
                    >
                      <img src={icon} alt={label} className="object-contain shrink-0" style={{ height: iconH, width: 'auto' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 leading-none mb-0.5">
                          {label}
                        </p>
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {title}
                        </p>
                        {subtitle ? (
                          <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
                        ) : null}
                      </div>
                      {count != null && count > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#1E3A5F]/10 text-[#1E3A5F]">
                          {count}
                        </span>
                      )}
                      <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {open && (
                      <div className="border-t border-slate-100">
                        {children}
                      </div>
                    )}
                  </div>
                )
              }

              const ParentRows = () => (
                <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                  {parentEquipment.map(rel => {
                    const pKind = getEquipmentKind((rel.main_equipment as any)?.categories_v2?.code)
                    const pIcon = getKindIcon(pKind)
                    return (
                      <button
                        key={rel.id}
                        onClick={() => handleNavigateToSubEquipment(rel.main_equipment_id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left group"
                      >
                        <img
                          src={pIcon.src}
                          alt=""
                          className="object-contain shrink-0 opacity-80"
                          style={{ height: Math.min(pIcon.h, 32), width: 'auto' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#1E3A5F] leading-none mb-1">
                            Montert på
                          </p>
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {rel.main_equipment?.serial_number || 'Ukjent SN'}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{rel.main_equipment?.name || ''}</p>
                        </div>
                        <ChevronRight size={14} className="text-slate-300 group-hover:text-[#1E3A5F] transition-colors shrink-0" />
                      </button>
                    )
                  })}
                </div>
              )

              return (
                <div className="flex flex-col gap-2">
                  {/* Primary section — matches the equipment's own type */}
                  <Section
                    id={kind}
                    icon={primaryIcon.src}
                    iconH={primaryIcon.h}
                    label={
                      kind === 'unit'
                        ? [
                            'Hovedutstyr',
                            currentEquipment.name || null,
                            currentEquipment.owner_id || null,
                            currentEquipment.part_number ? `PN ${currentEquipment.part_number}` : null,
                          ].filter(Boolean).join(' · ')
                        : primaryIcon.label
                    }
                    serial={currentEquipment.serial_number || currentEquipment.asset_number}
                    name={kind === 'unit' ? null : currentEquipment.name}
                    subtitle={
                      kind === 'unit'
                        ? (() => {
                            const specItem = (key: string, value: string, colorCls?: string) => (
                              <span key={key}>
                                <span className="font-semibold text-slate-500">{key}:</span>{' '}
                                <span className={colorCls}>{value}</span>
                              </span>
                            )

                            const nextNdt = unitHeaderSpecs.nextNdt || currentEquipment.next_ndt_date
                            let ndtItem: React.ReactNode = null
                            if (nextNdt) {
                              const today = new Date()
                              today.setHours(0, 0, 0, 0)
                              const ndtDate = new Date(nextNdt)
                              const in12Months = new Date(today)
                              in12Months.setFullYear(in12Months.getFullYear() + 1)
                              const isOverdue = ndtDate < today
                              const isSoon = !isOverdue && ndtDate <= in12Months
                              const cls = isOverdue
                                ? 'text-red-500 font-semibold'
                                : isSoon
                                  ? 'text-amber-400 font-semibold'
                                  : ''
                              ndtItem = specItem('Neste NDT', format(ndtDate, 'dd.MM.yyyy'), cls)
                            }

                            const allItems: React.ReactNode[] = [
                              specItem('Tare', unitHeaderSpecs.tare || '—'),
                              specItem('Payload', unitHeaderSpecs.payload || '—'),
                              specItem('MGW', unitHeaderSpecs.mgw || '—'),
                              ...(ndtItem ? [ndtItem] : []),
                              ...(unitHeaderSpecs.certificate ? [specItem('Sertifikat', unitHeaderSpecs.certificate)] : []),
                            ]

                            return (
                              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                                {allItems}
                              </div>
                            )
                          })()
                        : null
                    }
                  >
                    <div className="p-3">
                      <SpecificationsPanel
                        equipmentId={currentEquipmentId}
                        categoryId={currentEquipment.category_id}
                        identityEquipment={currentEquipment}
                        source={specSource}
                        onIdentityUpdate={(updated) => setCurrentEquipment((prev) => ({ ...prev, ...updated }))}
                        onSpecsUpdated={(change) => void handleSpecsUpdated(change, currentEquipmentId)}
                      />
                    </div>
                  </Section>

                  {/* Parent equipment — only when this item is mounted on one */}
                  {!isMainEquipment && parentEquipment.length > 0 && (
                    <ParentRows />
                  )}

                  {/* Linked sub-equipment — one row each */}
                  {isMainEquipment && [...slings, ...shackles, ...otherTools].map(rel => (
                    <RelatedEquipmentRow
                      key={rel.id}
                      rel={rel}
                      typeLabel={getRelationLabel(rel.categoryCode)}
                    />
                  ))}

                  {isMainEquipment && (
                    <button
                      type="button"
                      onClick={() => {
                        setReplacingRelationshipId(null)
                        setShowSubEquipmentModal(true)
                      }}
                      className="flex items-center justify-center border border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-[#1E3A5F] hover:text-[#1E3A5F] hover:bg-slate-50 transition-colors"
                      style={{ minHeight: 48 }}
                      title="Legg til underutstyr"
                    >
                      <Plus size={20} />
                    </button>
                  )}
                </div>
              )
            })()}

          </div>

          <div className={[
            'min-w-0 overflow-y-auto px-6 py-5',
            activeTab === 'dokumenter' ? 'flex-1' : 'hidden',
            fullscreen ? 'lg:block lg:flex-none lg:w-[26rem] xl:w-[30rem] lg:border-l lg:border-slate-200 lg:bg-slate-50/60' : '',
          ].join(' ')}>
            {fullscreen && (
              <div className="hidden lg:flex items-center gap-2 mb-3">
                <FileText size={17} className="text-[#1E3A5F]" />
                <p className="text-sm font-semibold text-slate-800">Dokumenter</p>
                {documentation.length > 0 && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#1E3A5F]/10 text-[#1E3A5F]">
                    {documentation.length}
                  </span>
                )}
              </div>
            )}

            {/* DOKUMENTER */}
            {(() => {
              const DOC_SECTIONS: { key: string; label: string; icon: string }[] = [
                { key: 'control_card',              label: 'Kontrollkort',      icon: docIconDoc  },
                { key: 'certificate',               label: 'Sertifikat',        icon: docIconCert },
                { key: 'declaration_of_conformity', label: 'Samsvarserklæring', icon: docIconCo   },
                { key: 'ndt_report',                label: 'NDT-rapport',       icon: docIconNdt  },
                { key: 'service_report',            label: 'Servicerapport',    icon: docIconSr   },
                { key: 'user_manual',               label: 'Brukermanual',      icon: docIconMan  },
                { key: 'other',                     label: 'Annet',             icon: docIconDoc  },
              ]

              const openUploadFor = (type: string) => {
                setSelectedDocumentType(type)
                setShowUploadDialog(true)
              }

              return (
                <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                  {DOC_SECTIONS.map(({ key, label, icon }) => {
                    const docs = documentation.filter(d => d.document_type === key)
                    const cols = 'grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-2 px-4'
                    return (
                      <div key={key}>
                        <div className={`${cols} py-1.5 bg-slate-50`}>
                          <img src={icon} alt="" className="h-4 w-5 object-contain opacity-70 justify-self-center" />
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 truncate leading-none">
                              {label}
                            </p>
                            {docs.length > 0 && (
                              <span className="text-xs font-semibold leading-none px-1.5 py-0.5 rounded-full bg-[#1E3A5F]/10 text-[#1E3A5F] shrink-0">
                                {docs.length}
                              </span>
                            )}
                          </div>
                          <div className="flex justify-end items-center">
                            {key !== 'control_card' && (
                              <button
                                type="button"
                                onClick={() => openUploadFor(key)}
                                className="inline-flex items-center gap-1 px-3 min-h-[40px] rounded-md text-sm font-medium text-[#1E3A5F] hover:bg-[#1E3A5F]/5 active:bg-[#1E3A5F]/10 transition-colors"
                              >
                                <Plus size={16} className="shrink-0" />
                                Last opp
                              </button>
                            )}
                          </div>
                        </div>

                        {docs.length > 0 ? (
                          docs.map((doc) => (
                            <a
                              key={doc.id}
                              href={documentUrls[doc.id] || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${cols} py-2.5 hover:bg-slate-50 transition-colors group border-t border-slate-100`}
                              style={{ minHeight: 48 }}
                            >
                              <img src={icon} alt="" className="h-4 w-5 object-contain justify-self-center" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 truncate group-hover:text-[#1E3A5F] transition-colors">
                                    {doc.file_name}
                                  </p>
                                  {doc.inspection_status && (
                                    <Badge variant={
                                      doc.inspection_status === 'OK' ? 'success' :
                                      doc.inspection_status === 'NC' ? 'danger' : 'warning'
                                    } size="sm">
                                      {doc.inspection_status}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">
                                  v{doc.version ?? '1.0'} · {format(new Date(doc.created_at), 'dd.MM.yyyy')}
                                </p>
                              </div>
                              <ChevronRight size={14} className="text-slate-300 group-hover:text-[#1E3A5F] transition-colors justify-self-end" />
                            </a>
                          ))
                        ) : (
                          <div className={`${cols} py-2 border-t border-slate-100`}>
                            <span />
                            <p className="text-xs text-slate-300">Ingen dokumenter</p>
                            <span />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })()}

          </div>
          </div>
        )}

        {/* ── Footer actions ────────────────────────────────────── */}
        <div className="border-t border-slate-200 px-6 py-4 flex items-center gap-3 bg-slate-50 shrink-0">
          {!isSLB && (
            <button
              onClick={openCopyDialog}
              className="flex items-center justify-center gap-2 px-4 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-md hover:bg-slate-50 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors"
              style={{ minHeight: 48 }}
              title="Kopier utstyr med nytt serienummer"
            >
              <CopyPlus size={16} />
              Kopier
            </button>
          )}
          <button
            onClick={() => setShowInspectionModal(true)}
            className="flex-1 flex items-center justify-center gap-2 px-4 bg-[#1E3A5F] text-white text-sm font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm"
            style={{ minHeight: 48 }}
          >
            <ClipboardList size={16} />Ny inspeksjon
          </button>
          {isAdmin && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center justify-center gap-2 px-4 bg-white border border-red-200 text-red-600 text-sm font-semibold rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
              style={{ minHeight: 48 }}
            >
              {deleting
                ? <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                : <Trash2 size={16} />}
              Slett
            </button>
          )}
        </div>

      {/* Inspection Modal */}
      {showInspectionModal && (
        <InspectionModal
          isOpen={showInspectionModal}
          onClose={() => {
            setShowInspectionModal(false)
            // Refresh equipment details after closing inspection modal
            fetchEquipmentDetails(currentEquipmentId)
          }}
          equipment={{
            id: currentEquipment.id,
            name: currentEquipment.name,
            asset_number: currentEquipment.asset_number,
            serial_number: currentEquipment.serial_number,
            owner_id: currentEquipment.owner_id,
            status: currentEquipment.status,
            categoryCode: currentCategoryCode,
          }}
          isSTMIDBEquipment={isSLB}
        />
      )}

      {/* Sub-Equipment Modal */}
      {showSubEquipmentModal && companyId && (
        <SubEquipmentModal
          isOpen={showSubEquipmentModal}
          initialMode="connect"
          excludeSubEquipmentId={
            replacingRelationshipId
              ? subEquipment.find(r => r.id === replacingRelationshipId)?.sub_equipment_id ?? null
              : null
          }
          replaceCategoryCode={
            replacingRelationshipId
              ? subEquipment.find(r => r.id === replacingRelationshipId)?.categoryCode ?? null
              : null
          }
          onClose={() => {
            setShowSubEquipmentModal(false)
            setReplacingRelationshipId(null)
          }}
          mainEquipmentId={currentEquipmentId}
          mainEquipmentName={currentEquipment.name}
          companyId={companyId}
          source={specSource}
          onSuccess={async () => {
            // If replacing, delete the old relationship after the new one is connected
            if (replacingRelationshipId) {
              if (isSLB) {
                await STMIDBEquipmentService.setParentEquipment(replacingRelationshipId, null)
              } else {
                await supabase
                  .from('equipment_relationship_v2')
                  .delete()
                  .eq('id', replacingRelationshipId)
              }
              setReplacingRelationshipId(null)
            }
            fetchEquipmentDetails(currentEquipmentId)
          }}
        />
      )}

      {/* Document Scanner */}
      {showDocumentScanner && (
        <DocumentScanner
          documentName={`${equipment.serial_number || equipment.asset_number || 'scan'}`}
          onComplete={(pdfFile) => {
            setSelectedFile(pdfFile)
            setShowDocumentScanner(false)
          }}
          onCancel={() => setShowDocumentScanner(false)}
        />
      )}

      </div>

      {/* Upload dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-lg w-full max-w-md overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-800">Last opp dokument</h3>
              <button
                onClick={() => { setShowUploadDialog(false); setSelectedFile(null); setSelectedDocumentType('certificate') }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Document type */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                  Dokumenttype
                </label>
                <select
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white"
                  style={{ minHeight: 48 }}
                >
                  <option value="certificate">Sertifikat</option>
                  <option value="declaration_of_conformity">Samsvarserklæring</option>
                  <option value="user_manual">Brukermanual</option>
                  <option value="service_report">Servicerapport</option>
                  <option value="ndt_report">NDT-rapport</option>
                  <option value="shortlist">Kortliste</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              {/* Source */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                  Kilde
                </label>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowUploadDialog(false); setTimeout(() => setShowDocumentScanner(true), 100) }}
                    className="flex items-center gap-3 px-4 py-3 border border-emerald-200 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors"
                    style={{ minHeight: 52 }}
                  >
                    <div className="w-8 h-8 bg-emerald-600 rounded-md flex items-center justify-center shrink-0">
                      <Camera size={18} className="text-white" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-semibold text-emerald-800 block">Skann med kamera</span>
                      <span className="text-xs text-emerald-600">Ta bilde → PDF</span>
                    </div>
                  </button>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-100" />
                    <span className="text-xs text-slate-400 font-semibold">eller</span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <label className="cursor-pointer">
                      <input type="file" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden" />
                      <div className="px-3 py-3 border border-slate-200 rounded-md hover:border-[#1E3A5F] hover:bg-slate-50 transition-colors text-center">
                        <FileText size={20} className="mx-auto mb-1 text-[#1E3A5F]" />
                        <span className="text-xs font-semibold text-slate-700 block">Dokument</span>
                        <span className="text-[10px] text-slate-400">PDF, Word, Excel</span>
                      </div>
                    </label>
                    <label className="cursor-pointer">
                      <input type="file" onChange={handleFileSelect} accept="image/*" className="hidden" />
                      <div className="px-3 py-3 border border-slate-200 rounded-md hover:border-[#1E3A5F] hover:bg-slate-50 transition-colors text-center">
                        <Upload size={20} className="mx-auto mb-1 text-[#1E3A5F]" />
                        <span className="text-xs font-semibold text-slate-700 block">Bilde</span>
                        <span className="text-[10px] text-slate-400">JPG, PNG</span>
                      </div>
                    </label>
                  </div>
                </div>

                {selectedFile && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
                    <span className="text-emerald-600 text-sm">✓</span>
                    <span className="text-sm text-slate-700 truncate">{selectedFile.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 px-5 py-4 flex gap-3 bg-slate-50">
              <button
                onClick={() => { setShowUploadDialog(false); setSelectedFile(null); setSelectedDocumentType('certificate') }}
                disabled={uploadingFile}
                className="flex-1 px-4 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
                style={{ minHeight: 48 }}
              >
                Avbryt
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploadingFile}
                className="flex-1 flex items-center justify-center gap-2 px-4 bg-[#1E3A5F] text-white text-sm font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm disabled:opacity-50"
                style={{ minHeight: 48 }}
              >
                {uploadingFile ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Laster opp...</span></>
                ) : (
                  <><Upload size={16} /><span>Last opp</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleCopy()
            }}
            className="bg-white rounded-lg w-full max-w-md overflow-hidden shadow-xl"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-800">Kopier utstyr</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {currentEquipment.name}
                  {currentEquipment.serial_number ? ` · SN ${currentEquipment.serial_number}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !copying && setShowCopyDialog(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-slate-500">
                Type, spesifikasjoner og lokasjon kopieres. Angi unikt serienummer for det nye utstyret.
                Dokumenter, NDT-datoer og underutstyr kopieres ikke.
              </p>

              {copyError && (
                <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3">
                  <p className="text-sm font-medium text-red-700">{copyError}</p>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                  Nytt serienummer *
                </label>
                <input
                  type="text"
                  value={copySerial}
                  onChange={e => { setCopySerial(e.target.value); if (copyError) setCopyError(null) }}
                  placeholder="SN-12345"
                  autoFocus
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800"
                  style={{ minHeight: 48 }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                    Eiers ID
                  </label>
                  <input
                    type="text"
                    value={copyOwnerId}
                    onChange={e => setCopyOwnerId(e.target.value)}
                    placeholder="Eiers ID"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800"
                    style={{ minHeight: 48 }}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                    Delenummer
                  </label>
                  <input
                    type="text"
                    value={copyPartNumber}
                    onChange={e => setCopyPartNumber(e.target.value)}
                    placeholder="PN-12345"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800"
                    style={{ minHeight: 48 }}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 px-5 py-3 flex items-center gap-3 bg-slate-50">
              <button
                type="button"
                onClick={() => setShowCopyDialog(false)}
                disabled={copying}
                className="flex-1 flex items-center justify-center h-12 px-4 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={copying || !copySerial.trim()}
                className="flex-1 flex items-center justify-center gap-2 h-12 px-4 text-sm font-semibold text-white bg-[#0D9488] rounded-md hover:bg-[#0B7A70] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copying
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Oppretter...</span></>
                  : <><CopyPlus size={16} /><span>Opprett kopi</span></>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default EquipmentDetailsModal

